import type {LDContextCommon, LDMultiKindContext} from 'launchdarkly-react-client-sdk';

export type LaunchDarklyIdentity = {
  userId: string;
  email?: string;
  name?: string;
  customer?: string;
};

/**
 * Builds the LaunchDarkly multi-context used for flag evaluation.
 *
 * Two context kinds are sent so flags can be targeted at either level from the LaunchDarkly
 * dashboard without any code change:
 * - `user`, keyed by the backend-issued `userId` and carrying `email`, so individual users and
 *   rule-based segments (e.g. "email ends with maddox.ai" for internal users) can be targeted.
 * - `organization`, keyed by `customer`, for the customer-wide rollouts we already do.
 *
 * The function is pure and deterministic on purpose: LaunchDarkly needs stable context keys for
 * percentage-rollout stickiness and honest context counts, and it keeps this unit-testable.
 *
 * @param identity the current user's identity as stored in Redux
 * @returns the multi-context, or `undefined` while the user is not identified yet
 */
export function buildLaunchDarklyContext(identity: LaunchDarklyIdentity): LDMultiKindContext | undefined {
  const {userId, email, name, customer} = identity;

  if (!userId) {
    return undefined;
  }

  const user: LDContextCommon = {
    key: userId,
    ...(email ? {email} : {}),
    ...(name || email ? {name: name || email} : {}),
    ...(customer ? {customer} : {}),
  };

  const context: LDMultiKindContext = {kind: 'multi', user};

  if (customer) {
    // `customer` is kept as an attribute (and not only as the key) so pre-existing targeting rules
    // written against `organization.customer` keep matching.
    context.organization = {key: customer, name: customer, customer};
  }

  return context;
}
